'use client';

import { use } from 'react';

import styled from '@emotion/styled';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Accordion from '@radix-ui/react-accordion';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { SurveyThemeContext } from '@/shared-utilities';
import { type GetProjectQuery } from '@/survey-graphql';

import SurveyAiFormsAiSectionMobileHeader from './survey-ai-forms-ai-section-mobile-header';
import SurveyAiFormsFormSectionQuestionCard from './survey-ai-forms-form-section-question-card';
import SurveyAiFormsFormSectionValidationSummaryCard from './survey-ai-forms-form-section-validation-summary-card';
import { useFormSection } from './survey-ai-forms-form-section.hooks';
import { useFormSectionScroll } from './survey-ai-forms-form-section.hooks';
import SurveyAiFormsSubmitButton from './survey-ai-forms-submit-button';
import { useSwipeGesture } from './survey-ai-forms-swipe.hooks';
import { getUnansweredRequiredQuestions } from './survey-ai-forms.helpers';

const ThemedDiv = styled.div<{ themeStyles: Record<string, string> }>`
  ${(props) => props.themeStyles}
`;

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface Props {
  questions: ProjectQuestion[];
  currentQuestionId: string | null;
  onQuestionFocus: (questionId: string) => void;
  onSubmit: () => void;
  onViewModeToggle?: () => void;
  className?: string;
  progress: number;
  estimatedTotalTimeInSeconds: number;
  viewMode: 'form';
  isSubmitting?: boolean;
  submittedQuestionIds: Set<string>;
  canSubmit?: boolean;
  validationErrors: Set<string>;
  hasTriedSubmit: boolean;
  isPreviewMode?: boolean;
  isAIFormAssistanceEnabled?: boolean;
}

export default function SurveyAiFormsFormSectionMobile({
  questions,
  currentQuestionId,
  onQuestionFocus,
  onSubmit,
  onViewModeToggle,
  className = '',
  progress,
  estimatedTotalTimeInSeconds,
  viewMode,
  isSubmitting = false,
  submittedQuestionIds,
  canSubmit = false,
  validationErrors,
  hasTriedSubmit,
  isPreviewMode = false,
  isAIFormAssistanceEnabled = true,
}: Props) {
  const { input: themeInput, styles } = use(SurveyThemeContext);

  const t = useTranslations('AIForms');

  const { responses } = useFormSection({ questions });

  const { scrollContainerRef, setQuestionRef } = useFormSectionScroll({
    currentQuestionId,
    responses,
  });

  // Get unanswered required questions for the summary card
  const unansweredRequiredQuestions = getUnansweredRequiredQuestions(
    questions,
    responses
  );

  const themeSettings = themeInput?.themeSettings;

  // Only show validation UI if submit was tried and there are unanswered questions
  const showValidationSummary =
    hasTriedSubmit && unansweredRequiredQuestions.length > 0;

  // Swipe gesture for drag handle - swipe down to toggle to AI mode
  const swipeHandlers = useSwipeGesture({
    onSwipeDown: onViewModeToggle,
    threshold: 30, // 30px threshold for drag handle
  });

  const hasBackgroundImage = !!(
    themeSettings?.bgImage || styles['--brand-bg-image']
  );

  return (
    <div
      data-test='form-section-mobile'
      className={cn('flex h-screen w-full flex-col', className)}
    >
      {/* Custom corner cutout - only show when AI form assistance is enabled */}
      {isAIFormAssistanceEnabled && (
        <ThemedDiv
          data-test='form-section-mobile-cutout'
          data-has-background-image={hasBackgroundImage}
          data-is-ai-enabled={isAIFormAssistanceEnabled}
          data-is-preview-mode={isPreviewMode}
          className='fixed top-16 right-2 z-50 size-16 bg-(image:--brand-bg-image) bg-auto bg-bottom-right bg-no-repeat data-[has-background-image=false]:bg-(--brand-bg-color) data-[is-ai-enabled=true]:hidden data-[is-preview-mode=true]:size-14'
          themeStyles={styles}
        />
      )}

      {/* Fixed Header - Turn on AI Button - only show when AI form assistance is enabled */}
      {isAIFormAssistanceEnabled && (
        <ThemedDiv
          data-test='form-section-mobile-header'
          data-has-background-image={hasBackgroundImage}
          className='fixed inset-x-0 top-0 z-50 flex h-16 flex-col items-center justify-between rounded-4xl rounded-br-none bg-(image:--brand-bg-image) bg-cover bg-center bg-no-repeat p-6 data-[has-background-image=false]:bg-(--brand-bg-color)'
          themeStyles={styles}
        >
          <div className='relative z-10 flex h-16 items-center justify-center'>
            <button
              onClick={onViewModeToggle}
              data-test='turn-on-ai-button'
              className='flex items-center gap-2 bg-transparent px-4 py-2 transition-colors'
            >
              <FontAwesomeIcon
                icon={faSparkles}
                className='size-4 text-(--brand-secondary)'
              />
              <span className='[font-family:var(--brand-font)] text-base font-semibold text-(--brand-secondary)'>
                {t('modeToggle.turnOnAi')}
              </span>
            </button>
          </div>
        </ThemedDiv>
      )}

      {/* Drag handle indicator - swipe down to toggle to AI mode - only show when AI form assistance is enabled */}
      {isAIFormAssistanceEnabled && (
        <div
          className='fixed top-16 left-1/2 z-50 h-1 w-12 -translate-x-1/2 cursor-grab items-center justify-center rounded-lg bg-gray-300 active:cursor-grabbing'
          onTouchStart={swipeHandlers.onTouchStart}
          onTouchMove={swipeHandlers.onTouchMove}
          onTouchEnd={swipeHandlers.onTouchEnd}
          onPointerDown={swipeHandlers.onPointerDown}
          onPointerMove={swipeHandlers.onPointerMove}
          onPointerUp={swipeHandlers.onPointerUp}
        />
      )}

      {/* Scrollable Content Area */}
      <div
        className={cn(
          'mx-2 flex flex-1 flex-col bg-white',
          isAIFormAssistanceEnabled ? 'mt-16' : 'mt-2'
        )}
      >
        {/* Mobile Header - Same as AI section mobile */}
        <div className='w-full pt-6.5'>
          <SurveyAiFormsAiSectionMobileHeader
            progress={progress}
            estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
            viewMode={viewMode}
            onViewModeToggle={onViewModeToggle}
            isFormSection={true}
          />
        </div>

        {/* Scrollable Questions Content */}
        <div
          ref={scrollContainerRef}
          data-test='form-section-mobile-scroll-container'
          className='flex flex-1 scrollbar-none flex-col overflow-y-auto px-4 pb-8 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
        >
          {/* Questions List with Accordion */}
          <Accordion.Root
            type='single'
            value={currentQuestionId || undefined}
            onValueChange={(value) => {
              if (value) {
                onQuestionFocus(value);
              }
            }}
            collapsible
            className='space-y-4'
          >
            {questions.map((question, index) => (
              <Accordion.Item
                key={question.id}
                value={question.id}
                ref={(el) => setQuestionRef(question.id, el)}
                className='relative overflow-visible'
              >
                <SurveyAiFormsFormSectionQuestionCard
                  question={question}
                  response={responses.get(question.id)}
                  isActive={currentQuestionId === question.id}
                  onFocus={onQuestionFocus}
                  isFormSectionMinimized={false}
                  currentQuestionIndex={index + 1}
                  totalQuestions={questions.length}
                  submittedQuestionIds={submittedQuestionIds}
                  hasValidationError={validationErrors.has(question.id)}
                />
              </Accordion.Item>
            ))}
          </Accordion.Root>

          {/* Validation Summary Card - Shown when there are unanswered required questions */}
          {showValidationSummary && (
            <SurveyAiFormsFormSectionValidationSummaryCard
              unansweredRequiredQuestions={unansweredRequiredQuestions}
            />
          )}

          {/* Submit Button */}
          <div className='mt-8 flex justify-end'>
            <SurveyAiFormsSubmitButton
              onClick={onSubmit}
              disabled={!canSubmit}
              label={t('submit.mobileLabel')}
              loading={isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
